import React, { useState, useEffect } from 'react';
import { fetchAuth, generatePdfBill } from '../../utils/api';

// Use relative paths — fetchAuth handles the base URL

const getStockStatus = (stock) => {
    if (stock <= 0) return { level: 'empty', label: 'Out of Stock' };
    if (stock <= 50) return { level: 'low', label: 'Low Stock' };
    return { level: 'healthy', label: 'In Stock' };
};

function PointOfSaleView({ user, handleApiError, showNotification }) {
    const [products, setProducts] = useState([]);
    const [cart, setCart] = useState([]);
    const [billSettings, setBillSettings] = useState(null);
    const [customerPhone, setCustomerPhone] = useState('');
    const [customerName, setCustomerName] = useState('');
    const [selectedCustomerId, setSelectedCustomerId] = useState(null);
    const [customerSearchResults, setCustomerSearchResults] = useState([]);
    const [saleStatus, setSaleStatus] = useState('pending');
    const [lastSaleDetails, setLastSaleDetails] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState('Cash');
    const [productSearch, setProductSearch] = useState('');
    const [myBillingProfile, setMyBillingProfile] = useState(null); // logged-in user's billing overrides
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [showInvoiceOnBill, setShowInvoiceOnBill] = useState(true);

    const fetchNextInvoiceNumber = async () => {
        try {
            const res = await fetchAuth('/api/bill-settings/peek-invoice');
            if (res.ok) {
                const data = await res.json();
                setInvoiceNumber(data.invoiceNumber);
            }
        } catch (err) { console.error('Failed to fetch invoice number:', err); }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [productsRes, settingsRes, usersRes] = await Promise.all([
                    fetchAuth('/api/product-variants'),
                    fetchAuth('/api/bill-settings'),
                    fetchAuth('/api/users'),  // to get logged-in user's billing profile
                ]);
                if (productsRes.ok) setProducts(await productsRes.json());
                if (settingsRes.ok) setBillSettings(await settingsRes.json());
                if (usersRes.ok) {
                    const users = await usersRes.json();
                    const me = users.find(u => u.username === user?.username);
                    if (me) {
                        setMyBillingProfile({
                            billingName: me.billingName || me.billing_name || null,
                            billingAddress: me.billingAddress || me.billing_address || null,
                            billingContact: me.billingContact || me.billing_contact || null,
                        });
                    }
                }
            } catch (err) { handleApiError(err); }
        };
        fetchData();
        fetchNextInvoiceNumber();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleApiError, user]);

    const handleCustomerSearch = async (query, type) => {
        if (query.length < 2) {
            setCustomerSearchResults([]);
            return;
        }
        try {
            const res = await fetchAuth(`/api/customers/search?${type}=${query}`);
            if (res.ok) setCustomerSearchResults(await res.json());
        } catch (err) { console.error("Customer search failed:", err); }
    };

    const handlePhoneChange = (phone) => {
        setCustomerPhone(phone);
        setCustomerName('');
        setSelectedCustomerId(null);
        handleCustomerSearch(phone, 'phone');
    };

    const handleNameChange = (name) => {
        setCustomerName(name);
        setCustomerPhone('');
        setSelectedCustomerId(null);
        handleCustomerSearch(name, 'name');
    };

    const selectCustomer = (customer) => {
        setCustomerPhone(customer.phone);
        setCustomerName(customer.name);
        setSelectedCustomerId(customer._id);
        setCustomerSearchResults([]);
    };

    const addToCart = (product) => {
        setCart(currentCart => {
            const existingItem = currentCart.find(item => item.productVariantId === product._id);
            if (existingItem) {
                return currentCart.map(item =>
                    item.productVariantId === product._id ? { ...item, quantity: item.quantity + 1 } : item
                );
            } else {
                return [...currentCart, {
                    productVariantId: product._id,
                    quantity: 1,
                    priceAtSale: product.salePrice,
                    nameAtSale: `${product.masterItem.name} (${product.variantName})`,
                    masterItemId: product.masterItem._id
                }];
            }
        });
    };

    const updateCartQuantity = (productId, newQuantity) => {
        let quantity = parseFloat(newQuantity);
        if (isNaN(quantity)) quantity = 0;

        if (quantity <= 0) {
            setCart(cart.filter(item => item.productVariantId !== productId));
        } else {
            setCart(cart.map(item => item.productVariantId === productId ? { ...item, quantity } : item));
        }
    };

    const updateCartPrice = (productId, newPrice) => {
        let priceAtSale = parseFloat(newPrice);
        if (isNaN(priceAtSale) || priceAtSale < 0) priceAtSale = 0;
        setCart(cart.map(item => item.productVariantId === productId ? { ...item, priceAtSale } : item));
    };

    const removeFromCart = (productId) => {
        setCart(cart.filter(item => item.productVariantId !== productId));
    };

    const totalAmount = cart.reduce((total, item) => total + (item.priceAtSale * item.quantity), 0);

    const handleFinalizeSale = async () => {
        if (cart.length === 0 || !customerName || !customerPhone) {
            showNotification('Customer details and cart items are required.', 'error');
            return;
        }
        const finalInvoiceNumber = invoiceNumber;
        const finalShowInvoice = showInvoiceOnBill;
        try {
            let customerId = selectedCustomerId;
            if (!customerId) {
                const customerRes = await fetchAuth('/api/customers/upsert', { method: 'POST', body: JSON.stringify({ name: customerName, phone: customerPhone }) });
                if (!customerRes.ok) throw new Error('Failed to save customer.');
                const customerData = await customerRes.json();
                customerId = customerData._id;
            }
            const saleData = { items: cart, totalAmount, customerId, paymentMethod, invoiceNumber: finalInvoiceNumber };
            const saleRes = await fetchAuth('/api/sales', { method: 'POST', body: JSON.stringify(saleData) });
            if (!saleRes.ok) throw new Error((await saleRes.json()).message);
            // Atomically increment the DB counter — must await to ensure the counter advances before the next peek
            await fetchAuth('/api/bill-settings/next-invoice', { method: 'POST' });
            showNotification('Sale Finalized Successfully!', 'success');
            setLastSaleDetails({ cart, customerName, customerPhone, total: totalAmount, invoiceNumber: finalInvoiceNumber, showInvoiceOnBill: finalShowInvoice });
            setSaleStatus('finalized');
        } catch (err) { handleApiError(err); }
    };


    // Merge logged-in user's billing profile into billSettings before printing
    const handlePrintBill = () => {
        if (!lastSaleDetails) return;
        const mergedSettings = {
            ...(billSettings || {}),
            ...(myBillingProfile || {}),  // override with user-specific billing if set
        };
        generatePdfBill(lastSaleDetails, mergedSettings);
    };
    const handleNewSale = () => {
        setCart([]);
        setCustomerName('');
        setCustomerPhone('');
        setSelectedCustomerId(null);
        setLastSaleDetails(null);
        setSaleStatus('pending');
        setShowInvoiceOnBill(true);
        fetchNextInvoiceNumber();
    };

    const filteredProducts = products.filter(p => `${p.masterItem.name} ${p.variantName}`.toLowerCase().includes(productSearch.toLowerCase()));

    return (
        <div className="pos-view">
            <div className="pos-billing-panel">
                <div className="pos-billing-header">
                    <h3>Current Bill</h3>
                    <div className="customer-input-group">
                        <input
                            type="text"
                            placeholder="Search by Phone Number..."
                            className="customer-input"
                            value={customerPhone}
                            onChange={e => handlePhoneChange(e.target.value)}
                            disabled={saleStatus === 'finalized'}
                        />
                        <input
                            type="text"
                            placeholder="Or Search by Customer Name..."
                            className="customer-input"
                            value={customerName}
                            onChange={e => handleNameChange(e.target.value)}
                            disabled={saleStatus === 'finalized'}
                        />
                        {customerSearchResults.length > 0 && (
                            <ul className="customer-search-results" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                {customerSearchResults.map(cust => (
                                    <li key={cust._id} onClick={() => selectCustomer(cust)}>
                                        {cust.name} - {cust.phone}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
                <div className="pos-cart-items">
                    {cart.length === 0 ? <p className="empty-cart-message">Cart is empty</p> : (
                        <table className="cart-table">
                            <thead><tr><th>Item</th><th>Qty</th><th>Price (₹)</th><th>Total (₹)</th><th></th></tr></thead>
                            <tbody>{cart.map(item => (
                                <tr key={item.productVariantId}>
                                    <td>{item.nameAtSale}</td>
                                    <td><input type="number" value={item.quantity} onChange={e => updateCartQuantity(item.productVariantId, e.target.value)} /></td>
                                    <td><input type="number" value={item.priceAtSale} onChange={e => updateCartPrice(item.productVariantId, e.target.value)} style={{ width: '80px', padding: '4px', border: '1px solid var(--color-border)', borderRadius: '4px' }} /></td>
                                    <td>₹{(item.priceAtSale * item.quantity).toFixed(2)}</td>
                                    <td>
                                        <button className="icon-btn delete" onClick={() => removeFromCart(item.productVariantId)} title="Remove Item">
                                            <svg viewBox="0 0 24 24"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"></path></svg>
                                        </button>
                                    </td>
                                </tr>
                            ))}</tbody>
                        </table>
                    )}
                </div>
                <div className="pos-billing-footer">
                    <div className="total-section"><h4>Grand Total</h4><span>₹{totalAmount.toFixed(2)}</span></div>
                    {saleStatus === 'pending' ? (
                        <>
                            <div className="invoice-options" style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '10px' }}>
                                <input
                                    type="text"
                                    placeholder="Loading invoice number..."
                                    className="customer-input"
                                    value={invoiceNumber}
                                    readOnly
                                    style={{ cursor: 'default', backgroundColor: 'var(--color-surface, #f5f5f5)', color: 'var(--color-text-muted, #888)' }}
                                />
                                <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <input
                                        type="checkbox"
                                        checked={showInvoiceOnBill}
                                        onChange={e => setShowInvoiceOnBill(e.target.checked)}
                                    />
                                    Show Invoice Number on Bill
                                </label>
                            </div>
                            <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} className="payment-select">
                                <option value="Cash">Cash</option>
                                <option value="Online">Online</option>
                                <option value="Credit">Credit (30-day due)</option>
                            </select>
                            <button className="finalize-btn" onClick={handleFinalizeSale}>Finalize Sale</button>
                        </>
                    ) : (
                        <div className="finalized-actions"><button className="print-btn" onClick={handlePrintBill}>Print Bill</button><button className="new-sale-btn" onClick={handleNewSale}>New Sale</button></div>
                    )}
                </div>
            </div>
            <div className="pos-product-panel">
                <input type="text" placeholder="Search products..." className="product-search-input" value={productSearch} onChange={e => setProductSearch(e.target.value)} />
                <div className="product-grid" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', paddingRight: '10px' }}>
                    {filteredProducts.map(p => {
                        const stock = p.masterItem?.stock || 0;
                        const status = getStockStatus(stock);
                        return (
                            <div key={p._id} className={`product-card stock-card-accent--${status.level}`} onClick={() => addToCart(p)}>
                                <p className="product-name">{p.masterItem.name} ({p.variantName})</p>
                                <p className="product-price">₹{p.salePrice ? p.salePrice.toFixed(2) : '0.00'}</p>
                                <span className={`stock-badge stock-badge--${status.level}`} style={{fontSize: '0.7rem', padding: '2px 8px', marginTop: '4px'}}>
                                    <span className={`stock-dot stock-dot--${status.level}`}></span>
                                    {stock} — {status.label}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

export default PointOfSaleView;
